import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "@phosphor-icons/react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import {
  fetchAccountMe,
  fetchAccountParticipationsMapped,
  fetchGiveawayById,
  fetchMyParticipation,
  fetchSbpBanks,
  formatStepBodyForDisplay,
  giveawayPhoneAuthInit,
  giveawayPhoneAuthVerify,
  marketplaceMaxWebappBindPhone,
  marketplaceTelegramWebappBindPhone,
  patchAccountMe,
  registerGiveawayParticipant,
  reportParticipationProgress,
  resolveKeywordForStep,
  submitParticipationComplete,
  deleteStepScreenshot,
  fetchStepScreenshots,
  uploadStepScreenshot,
} from "../api/client";
import {
  cacheGlobalProfileFromAccountMe,
  hasMarketplaceJwtCredentials,
  normalizePhoneDigits,
  readGlobalProfile,
  readTokenForGiveaway,
  saveGlobalProfile,
  saveMarketplaceTokens,
  saveTokenForGiveaway,
} from "../lib/wicoopProfileStorage";
import {
  readPhoneProofForGiveaway,
  savePhoneProofForGiveaway,
} from "../lib/wicoopPhoneProof";
import {
  ensureMarketplaceJwtForMaxParticipate,
  tryAuthenticateMaxWebApp,
  requestMaxWebAppContactPhone,
} from "../lib/maxWebAppAuth.js";
import {
  ensureMarketplaceJwtForTelegramParticipate,
  isInsideTelegramWebApp,
  requestTelegramWebAppContactPhone,
} from "../lib/telegramWebAppAuth.js";
import { useParticipateChrome } from "../context/ParticipateChromeContext.jsx";
import { fetchProfileCompletionRedirect } from "../utils/profileCompletion.js";
import { renderTextWithLinks } from "../lib/renderTextWithLinks.jsx";
import styles from "./ParticipatePage.module.css";

/** Форматирование РФ номера: +7 XXX XXX-XX-XX */
function formatPhoneRu(nationalDigits) {
  const d = String(nationalDigits).replace(/\D/g, "").slice(0, 10);
  if (!d) return "";
  const a = d.slice(0, 3);
  const b = d.slice(3, 6);
  const c = d.slice(6, 8);
  const e = d.slice(8, 10);
  let out = `+7 ${a}`;
  if (b) out += ` ${b}`;
  if (c) out += `-${c}`;
  if (e) out += `-${e}`;
  return out;
}

/** Из значения инпута — 10 цифр национальной части (без ведущей 7/8). */
function parsePhoneRu(raw) {
  let d = String(raw ?? "").replace(/\D/g, "");
  if (d.startsWith("8") && d.length >= 10) d = `7${d.slice(1)}`;
  if (d.startsWith("7")) d = d.slice(1);
  return d.slice(0, 10);
}

/** Перенос старых ключей wicoop_participant_v1/v2_* в общий профиль и карту токенов */
function migrateLegacyStorage(giveawayId) {
  for (const key of [
    `wicoop_participant_v2_${giveawayId}`,
    `wicoop_participant_v1_${giveawayId}`,
  ]) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const o = JSON.parse(raw);
      if (o?.phone && o?.first_name && o?.last_name) {
        saveGlobalProfile({
          phone: normalizePhoneDigits(o.phone),
          first_name: o.first_name,
          last_name: o.last_name,
          ...(o.sbp_bank_id != null && String(o.sbp_bank_id).trim() !== ""
            ? { sbp_bank_id: String(o.sbp_bank_id).trim() }
            : {}),
        });
        if (o.participant_token) {
          saveTokenForGiveaway(giveawayId, o.participant_token);
        }
      }
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
const ALLOWED_SCREENSHOT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

function mapScreenshotsByStep(list) {
  const map = {};
  for (const s of list || []) {
    const si = Number(s.step_index);
    if (!Number.isFinite(si)) continue;
    if (!map[si]) map[si] = [];
    map[si].push({ id: s.id, url: s.image_url ?? "" });
  }
  return map;
}

// Токен участия: сначала из localStorage, а если там нет (участие создано в боте или
// на другом устройстве) — подтягиваем из участий backend по giveawayId и кэшируем
// локально. Иначе открытие выключенной (is_active=false) раздачи падает с 404, а
// продолжить участие (шаги/скриншоты/отправка) невозможно без токена.
async function resolveGiveawayToken(giveawayId) {
  if (!giveawayId) return null;
  const local = readTokenForGiveaway(giveawayId);
  if (local) return local;
  if (!hasMarketplaceJwtCredentials()) return null;
  try {
    const rows = await fetchAccountParticipationsMapped();
    const match = Array.isArray(rows)
      ? rows.find((r) => String(r.giveawayId) === String(giveawayId) && r.verificationToken)
      : null;
    if (match?.verificationToken) {
      saveTokenForGiveaway(giveawayId, match.verificationToken);
      return match.verificationToken;
    }
  } catch {
    /* нет сети/участий — вернём null, обычный сценарий регистрации */
  }
  return null;
}

export default function ParticipatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [item, setItem] = useState(undefined);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);

  const [phase, setPhase] = useState("loading");
  const [phone, setPhone] = useState("");
  const [phoneConfirm, setPhoneConfirm] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  /** После отправки кода на почту (чтобы «Назад» с шага ФИО вёл на код) */
  const [emailCodeRequested, setEmailCodeRequested] = useState(false);
  const [jumpSbpEnabled, setJumpSbpEnabled] = useState(false);
  const [sbpBanks, setSbpBanks] = useState([]);
  const [sbpBankId, setSbpBankId] = useState("");
  const [authViaMax, setAuthViaMax] = useState(false);
  const [authViaTelegram, setAuthViaTelegram] = useState(false);
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [regError, setRegError] = useState(null);
  const [callBusy, setCallBusy] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneProof, setPhoneProof] = useState(null);
  const [assignedKeyword, setAssignedKeyword] = useState("");
  const [keywordCopied, setKeywordCopied] = useState(false);
  // Причина возврата на доработку (показываем баннером в шагах).
  const [reworkNote, setReworkNote] = useState("");

  const [participantToken, setParticipantToken] = useState(null);
  /** @type {[Record<number, { id: number, url: string }[]>, function]} */
  const [stepShots, setStepShots] = useState({});
  const [stepError, setStepError] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [finishBusy, setFinishBusy] = useState(false);

  const { setParticipateHeaderLine } = useParticipateChrome();
  const jwtAutoPhoneRef = useRef(false);
  /** Пропуск email+код (JWT или уже есть phone_proof в хранилище) */
  const skipEmailVerificationRef = useRef(false);
  const [skipEmailSteps, setSkipEmailSteps] = useState(false);

  const canSkipEmailVerification = authViaMax || authViaTelegram || skipEmailSteps;

  useEffect(() => {
    if (!item) {
      setParticipateHeaderLine(null);
      return () => setParticipateHeaderLine(null);
    }
    if (phase === "loading" || phase === "syncing") {
      setParticipateHeaderLine(null);
      return () => setParticipateHeaderLine(null);
    }
    if (
      phase === "phone" ||
      phase === "phone_confirm" ||
      phase === "email_request" ||
      phase === "email_code" ||
      phase === "last_name" ||
      phase === "first_name" ||
      phase === "bank"
    ) {
      const order = skipEmailSteps
        ? ["last_name", "first_name", ...(jumpSbpEnabled ? ["bank"] : [])]
        : [
            "phone",
            "phone_confirm",
            "email_request",
            "email_code",
            "last_name",
            "first_name",
            ...(jumpSbpEnabled ? ["bank"] : []),
          ];
      const i = order.indexOf(phase);
      const n = order.length;
      const label =
        i >= 0 ? `Регистрация · шаг ${i + 1} из ${n}` : "Регистрация";
      setParticipateHeaderLine(label);
      return () => setParticipateHeaderLine(null);
    }
    if (phase === "steps") {
      const n = item.steps?.length ?? 0;
      setParticipateHeaderLine(n > 0 ? `Этап ${index + 1} из ${n}` : null);
      return () => setParticipateHeaderLine(null);
    }
    if (phase === "submitted") {
      setParticipateHeaderLine("Заявка отправлена");
      return () => setParticipateHeaderLine(null);
    }
    setParticipateHeaderLine(null);
    return () => setParticipateHeaderLine(null);
  }, [item, phase, index, setParticipateHeaderLine, jumpSbpEnabled, skipEmailSteps]);

  useEffect(() => {
    if (phase !== "steps") return;
    if (!item?.id || !participantToken || !item.steps?.length) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchMyParticipation(item.id, participantToken);
        if (cancelled) return;
        if (!p?.participates) {
          // Token might be stale/invalid (e.g. cleared server-side). Force re-registration flow.
          saveTokenForGiveaway(item.id, null);
          setParticipantToken(null);
          setAssignedKeyword("");
          setSkipEmailSteps(false);
          setPhase("phone");
          return;
        }
        // Если модератор вернул на доработку — показываем причину баннером в шагах.
        setReworkNote(
          p.status_title === "Нужна доработка" && (p.status_subtitle || "").trim()
            ? String(p.status_subtitle).trim()
            : "",
        );
        if (
          p.session_status === "pending_review" ||
          p.session_status === "paid" ||
          p.can_continue === false
        ) {
          if (p.session_status === "pending_review" || p.session_status === "paid") {
            setPhase("submitted");
          }
          return;
        }
        const idx = Number(p.current_step_index);
        if (Number.isFinite(idx) && idx >= 0 && idx < item.steps.length) {
          setIndex(idx);
        }
        setAssignedKeyword(p?.assigned_keyword ? String(p.assigned_keyword) : "");
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, item?.id, item?.steps?.length, participantToken]);

  useEffect(() => {
    if (phase !== "steps") return;
    if (!item?.id || !participantToken) return;
    const kwIdx = item.keywordStepIndex ?? item.keyword_step_index;
    const isKeywordStep = Number.isFinite(Number(kwIdx)) && Number(kwIdx) === index;
    if (!isKeywordStep) return;
    if ((assignedKeyword || "").trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchMyParticipation(item.id, participantToken);
        if (cancelled) return;
        if (!p?.participates) {
          saveTokenForGiveaway(item.id, null);
          setParticipantToken(null);
          setAssignedKeyword("");
          setPhase("phone");
          return;
        }
        if (p?.assigned_keyword) setAssignedKeyword(String(p.assigned_keyword));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, item?.id, participantToken, item?.keywordStepIndex, item?.keyword_step_index, index, assignedKeyword]);

  useEffect(() => {
    if (phase !== "steps" || !item?.id || !participantToken) return;
    reportParticipationProgress(item.id, participantToken, index).catch(() => {});
  }, [phase, item?.id, participantToken, index]);

  useEffect(() => {
    if (phase !== "steps" || !item?.id || !participantToken) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchStepScreenshots(item.id, participantToken);
        if (cancelled) return;
        setStepShots(mapScreenshotsByStep(data.screenshots));
      } catch {
        if (!cancelled) setStepShots({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, item?.id, participantToken]);

  useEffect(() => {
    setIndex(0);
  }, [id]);

  useEffect(() => {
    setKeywordCopied(false);
  }, [index, id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Токен нужен, чтобы backend отдал даже выключенную (is_active=false) раздачу
        // и подставил {ключевое слово} в шаги. Берём из localStorage, а если нет —
        // из участий backend.
        const savedToken = await resolveGiveawayToken(id);
        const g = await fetchGiveawayById(id, savedToken);
        if (!cancelled) setItem(g);
      } catch (e) {
        if (!cancelled) setError(e.message ?? "Ошибка загрузки");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!item?.id) return;
    let cancelled = false;
    migrateLegacyStorage(item.id);

    (async () => {
      const tokenSaved = await resolveGiveawayToken(item.id);
      if (tokenSaved) {
        if (!cancelled) {
          setParticipantToken(tokenSaved);
          setPhase("steps");
        }
        return;
      }

      const maxContactBridge =
        typeof window !== "undefined" &&
        window.WebApp &&
        typeof window.WebApp.requestContact === "function";
      const telegramContactBridge =
        typeof window !== "undefined" &&
        window.Telegram?.WebApp &&
        typeof window.Telegram.WebApp.requestContact === "function";

      // MAX: если есть initData, сначала гарантированно получаем JWT витрины,
      // иначе UI откатится на email/телефонный сценарий.
      if (
        typeof window !== "undefined" &&
        window.WebApp?.initData &&
        String(window.WebApp.initData).trim()
      ) {
        if (!cancelled) setAuthViaMax(true);
        if (!hasMarketplaceJwtCredentials()) {
          try {
            await tryAuthenticateMaxWebApp();
          } catch {
            /* ignore */
          }
        }
      }

      await ensureMarketplaceJwtForMaxParticipate();
      await ensureMarketplaceJwtForTelegramParticipate();
      if (cancelled) return;

      if (isInsideTelegramWebApp() && hasMarketplaceJwtCredentials()) {
        if (!cancelled) {
          setAuthViaTelegram(true);
          setSkipEmailSteps(true);
          skipEmailVerificationRef.current = true;
        }
      }

      /** Последний /account/me/ при JWT — мини-приложения (привязка телефона) и обход email. */
      let meFromJwt = null;

      if ((maxContactBridge || telegramContactBridge) && hasMarketplaceJwtCredentials()) {
        try {
          let me = await fetchAccountMe();
          if (cancelled) return;
          if (!cancelled) setAuthViaMax(!!me?.auth_via_max);
          if (!cancelled && me?.auth_via_telegram) setAuthViaTelegram(true);
          if (me.auth_via_max && !normalizePhoneDigits(me.phone ?? "") && maxContactBridge) {
            const rawPhone = await requestMaxWebAppContactPhone();
            if (cancelled) return;
            me = await marketplaceMaxWebappBindPhone(rawPhone);
            if (!cancelled) cacheGlobalProfileFromAccountMe(me);
            if (!cancelled) setAuthViaMax(!!me?.auth_via_max);
          } else if (
            me.auth_via_telegram &&
            !normalizePhoneDigits(me.phone ?? "") &&
            telegramContactBridge
          ) {
            const rawPhone = await requestTelegramWebAppContactPhone();
            if (cancelled) return;
            me = await marketplaceTelegramWebappBindPhone(rawPhone);
            if (me?.access) {
              saveMarketplaceTokens({ access: me.access, refresh: me.refresh, phone: me.phone });
            }
            if (!cancelled) cacheGlobalProfileFromAccountMe(me);
          }
          meFromJwt = me;
        } catch {
          /* ручной ввод телефона и обычная регистрация */
        }
      }

      if (!meFromJwt && hasMarketplaceJwtCredentials()) {
        try {
          meFromJwt = await fetchAccountMe();
          if (!cancelled) cacheGlobalProfileFromAccountMe(meFromJwt);
          if (!cancelled) setAuthViaMax(!!meFromJwt?.auth_via_max);
          if (!cancelled && meFromJwt?.auth_via_telegram) setAuthViaTelegram(true);
        } catch {
          /* профиль подтянется на шаге телефона */
        }
      }

      let jumpEnabled = false;
      try {
        const sbpData = await fetchSbpBanks();
        if (!cancelled) {
          jumpEnabled = !!sbpData.jump_enabled;
          setJumpSbpEnabled(jumpEnabled);
          setSbpBanks(Array.isArray(sbpData.items) ? sbpData.items : []);
        }
      } catch {
        if (!cancelled) {
          setJumpSbpEnabled(false);
          setSbpBanks([]);
        }
      }

      const completionRedirect = await fetchProfileCompletionRedirect(
        `/giveaway/${id}/participate`
      );
      if (!cancelled && completionRedirect) {
        navigate(completionRedirect, { replace: true });
        return;
      }

      let profile = readGlobalProfile();
      const phoneFromJwt = meFromJwt ? normalizePhoneDigits(meFromJwt.phone ?? "") : "";
      if (
        !profile &&
        hasMarketplaceJwtCredentials() &&
        phoneFromJwt &&
        !readPhoneProofForGiveaway(item.id, phoneFromJwt)
      ) {
        profile = {
          phone: phoneFromJwt,
          first_name: (meFromJwt.first_name ?? "").trim(),
          last_name: (meFromJwt.last_name ?? "").trim(),
          email: (meFromJwt.email ?? "").trim(),
          ...(meFromJwt.sbp_bank_id != null && String(meFromJwt.sbp_bank_id).trim() !== ""
            ? { sbp_bank_id: String(meFromJwt.sbp_bank_id).trim() }
            : {}),
        };
      }

      if (profile) {
        const proof = readPhoneProofForGiveaway(item.id, profile.phone);
        const canUseJwtPhone = hasMarketplaceJwtCredentials() && !proof;
        const namesOk =
          (profile.first_name ?? "").trim().length > 0 &&
          (profile.last_name ?? "").trim().length > 0;
        const sbpOk =
          !jumpEnabled ||
          (profile.sbp_bank_id != null && String(profile.sbp_bank_id).trim() !== "");

            if (proof || canUseJwtPhone) {
          if (!cancelled) {
            setSkipEmailSteps(true);
            skipEmailVerificationRef.current = true;
          }
          if (!namesOk) {
            if (!cancelled) {
              if (proof) setPhoneProof(proof);
              setPhone(formatPhoneRu(parsePhoneRu(profile.phone)));
              setFirstName(profile.first_name);
              setLastName(profile.last_name);
              setEmail((profile.email ?? "").toString().trim());
              setSbpBankId(
                profile.sbp_bank_id != null ? String(profile.sbp_bank_id) : ""
              );
              setPhase("last_name");
            }
            return;
          }
          if (!sbpOk) {
            if (!cancelled) {
              if (proof) setPhoneProof(proof);
              setPhone(formatPhoneRu(parsePhoneRu(profile.phone)));
              setFirstName(profile.first_name);
              setLastName(profile.last_name);
              setEmail((profile.email ?? "").toString().trim());
              setSbpBankId(profile.sbp_bank_id != null ? String(profile.sbp_bank_id) : "");
              setPhase("bank");
            }
            return;
          }
          if (!cancelled) {
            setPhase("syncing");
            setRegError(null);
          }
          try {
            const regBody = {
              phone: profile.phone,
              first_name: profile.first_name,
              last_name: profile.last_name,
              email: (profile.email ?? "").toString().trim(),
            };
            if (proof) {
              regBody.phone_proof = proof;
            }
            if (profile.sbp_bank_id != null && String(profile.sbp_bank_id).trim() !== "") {
              regBody.sbp_bank_id = parseInt(String(profile.sbp_bank_id).trim(), 10);
            }
            const data = await registerGiveawayParticipant(item.id, regBody);
            if (cancelled) return;
            const token = data.verification_token ?? null;
            if (token) {
              saveTokenForGiveaway(item.id, token);
              saveGlobalProfile(profile);
              setParticipantToken(token);
              setPhase("steps");
              setIndex(0);
            } else {
              if (!cancelled) {
                setPhone(profile.phone);
                setFirstName(profile.first_name);
                setLastName(profile.last_name);
                setEmail((profile.email ?? "").toString().trim());
                if (profile.sbp_bank_id != null) {
                  setSbpBankId(String(profile.sbp_bank_id));
                }
                setSkipEmailSteps(false);
                setPhase("phone");
              }
            }
          } catch (e) {
            if (!cancelled) {
              setRegError(e.message ?? "Не удалось подключить профиль");
              setPhone(formatPhoneRu(parsePhoneRu(profile.phone)));
              setFirstName(profile.first_name);
              setLastName(profile.last_name);
              setEmail((profile.email ?? "").toString().trim());
              if (profile.sbp_bank_id != null) {
                setSbpBankId(String(profile.sbp_bank_id));
              }
              setSkipEmailSteps(false);
              setPhase("phone");
            }
          }
          return;
        }
      }

      if (!cancelled) {
        const p = readGlobalProfile();
        if (p) {
          setPhone(formatPhoneRu(parsePhoneRu(p.phone)));
          setFirstName(p.first_name);
          setLastName(p.last_name);
          if (p.sbp_bank_id != null) setSbpBankId(String(p.sbp_bank_id));
        }
        if (isInsideTelegramWebApp() && hasMarketplaceJwtCredentials()) {
          setAuthViaTelegram(true);
          setSkipEmailSteps(true);
          skipEmailVerificationRef.current = true;
        } else {
          setSkipEmailSteps(false);
        }
        setPhase("phone");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [item]);

  useEffect(() => {
    if (phase !== "phone") return;
    if (jwtAutoPhoneRef.current) return;
    const local = readGlobalProfile();
    if (local) {
      setPhone(formatPhoneRu(parsePhoneRu(local.phone)));
      setFirstName(local.first_name);
      setLastName(local.last_name);
      setEmail((local.email ?? "").toString().trim());
      setSbpBankId(local.sbp_bank_id != null ? String(local.sbp_bank_id) : "");
      return;
    }
    if (!hasMarketplaceJwtCredentials()) return;
    let cancelled = false;
    (async () => {
      try {
        const me = await fetchAccountMe();
        if (cancelled || !item?.id) return;
        if (me.phone) {
          jwtAutoPhoneRef.current = true;
          setPhone(formatPhoneRu(parsePhoneRu(me.phone)));
          if (me.first_name) setFirstName(me.first_name);
          if (me.last_name) setLastName(me.last_name);
          if (me.email) setEmail(String(me.email));
          if (me.sbp_bank_id != null) setSbpBankId(String(me.sbp_bank_id));
          setAuthViaMax(!!me?.auth_via_max);
          if (me?.auth_via_telegram || isInsideTelegramWebApp()) {
            setAuthViaTelegram(true);
          }
          setPhoneProof(null);
          setRegError(null);
          setSkipEmailSteps(true);
          skipEmailVerificationRef.current = true;
          const namesOk =
            (me.first_name ?? "").toString().trim().length > 0 &&
            (me.last_name ?? "").toString().trim().length > 0;
          const sbpOk =
            !jumpSbpEnabled ||
            (me.sbp_bank_id != null && String(me.sbp_bank_id).trim() !== "");
          if (namesOk && sbpOk) {
            setPhase("syncing");
            setRegError(null);
            try {
              const regBody = {
                phone: normalizePhoneDigits(formatPhoneRu(parsePhoneRu(me.phone))),
                first_name: me.first_name.toString().trim(),
                last_name: me.last_name.toString().trim(),
                email: (me.email ?? "").toString().trim(),
              };
              if (me.sbp_bank_id != null && String(me.sbp_bank_id).trim() !== "") {
                regBody.sbp_bank_id = parseInt(String(me.sbp_bank_id), 10);
              }
              const data = await registerGiveawayParticipant(item.id, regBody);
              if (cancelled) return;
              const token = data.verification_token ?? null;
              if (token) {
                saveTokenForGiveaway(item.id, token);
                saveGlobalProfile({
                  phone: normalizePhoneDigits(formatPhoneRu(parsePhoneRu(me.phone))),
                  first_name: me.first_name.toString().trim(),
                  last_name: me.last_name.toString().trim(),
                  email: (me.email ?? "").toString().trim(),
                  ...(jumpSbpEnabled && me.sbp_bank_id != null
                    ? { sbp_bank_id: String(me.sbp_bank_id) }
                    : {}),
                });
                setParticipantToken(token);
                setPhase("steps");
                setIndex(0);
              } else {
                setSkipEmailSteps(false);
                setPhase("phone");
              }
            } catch (e) {
              if (!cancelled) {
                setRegError(e.message ?? "Не удалось подключить профиль");
                setSkipEmailSteps(false);
                setPhase("phone");
              }
            }
          } else if (!namesOk) {
            setPhase("last_name");
          } else {
            setPhase("bank");
          }
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, item?.id, jumpSbpEnabled]);

  useEffect(() => {
    if (fileRef.current) fileRef.current.value = "";
    setStepError(null);
  }, [index]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [phase, index]);

  useEffect(() => {
    if (!item?.id) return;
    if (
      phase !== "last_name" &&
      phase !== "first_name" &&
      phase !== "bank" &&
      phase !== "email_code"
    ) {
      return;
    }
    if (phoneProof) return;
    const d = normalizePhoneDigits(phone);
    if (d.length < 10) return;
    const p = readPhoneProofForGiveaway(item.id, d);
    if (p) setPhoneProof(p);
  }, [phase, item?.id, phone, phoneProof]);

  const phoneDigits = normalizePhoneDigits(phone);
  const phoneConfirmDigits = normalizePhoneDigits(phoneConfirm);
  const phoneValid = phoneDigits.length >= 10;
  const phonesMatch = phoneValid && phoneConfirmDigits === phoneDigits;
  const emailTrim = email.trim();
  const emailValid = /^\S+@\S+\.\S+$/.test(emailTrim);

  function goBackFromLastName() {
    setRegError(null);
    if (skipEmailVerificationRef.current) {
      setPhase("phone");
      return;
    }
    if (emailCodeRequested) setPhase("email_code");
    else setPhase("phone_confirm");
  }

  function continueFromPhone() {
    setRegError(null);
    if (!phoneValid) {
      setRegError("Введите номер телефона (не менее 10 цифр).");
      return;
    }
    if (canSkipEmailVerification || skipEmailVerificationRef.current) {
      setPhoneConfirm(phone);
      setSkipEmailSteps(true);
      skipEmailVerificationRef.current = true;
      setPhase("last_name");
      return;
    }
    setPhoneConfirm("");
    setPhase("phone_confirm");
  }

  function continueFromPhoneConfirm() {
    setRegError(null);
    if (!phonesMatch) {
      setRegError("Номера не совпадают. Введите ещё раз.");
      return;
    }
    // Mini App (MAX / Telegram): почту не спрашиваем — номер уже подтверждён через JWT.
    if (canSkipEmailVerification || skipEmailVerificationRef.current) {
      setSkipEmailSteps(true);
      skipEmailVerificationRef.current = true;
      setPhase("last_name");
      return;
    }
    setEmailCodeRequested(false);
    setPhase("email_request");
  }

  async function sendEmailCodeForGiveaway() {
    setRegError(null);
    if (!phonesMatch) {
      setRegError("Сначала подтвердите номер на предыдущем шаге.");
      return;
    }
    if (!emailValid) {
      setRegError("Укажите корректный email.");
      return;
    }
    setCallBusy(true);
    try {
      await giveawayPhoneAuthInit(item.id, { phone: phoneDigits, email: emailTrim });
      setOtpCode("");
      setEmailCodeRequested(true);
      setPhase("email_code");
    } catch (e) {
      setRegError(e.message ?? "Не удалось отправить письмо");
    } finally {
      setCallBusy(false);
    }
  }

  async function submitEmailCodeForGiveaway() {
    setRegError(null);
    const digits = String(otpCode ?? "").replace(/\D/g, "");
    if (digits.length !== 4) {
      setRegError("Введите 4 цифры из письма.");
      return;
    }
    if (!emailValid) {
      setRegError("Укажите корректный email.");
      return;
    }
    setCallBusy(true);
    try {
      const data = await giveawayPhoneAuthVerify(item.id, {
        phone: phoneDigits,
        email: emailTrim,
        code: digits,
      });
      const pid = data.phone_proof;
      if (!pid) throw new Error("Нет подтверждения номера");
      setPhoneProof(pid);
      savePhoneProofForGiveaway(item.id, phoneDigits, pid);
      setPhase("last_name");
    } catch (e) {
      setRegError(e.message ?? "Неверный код");
    } finally {
      setCallBusy(false);
    }
  }

  async function submitRegistration() {
    setRegError(null);
    if (jumpSbpEnabled && phase !== "bank") return;
    if (!jumpSbpEnabled && phase !== "first_name") return;
    if (!phoneProof && !hasMarketplaceJwtCredentials()) {
      setRegError("Сначала подтвердите email и код из письма.");
      setPhase(skipEmailVerificationRef.current ? "phone" : "email_request");
      return;
    }
    if (!lastName.trim() || !firstName.trim()) {
      setRegError("Укажите фамилию и имя.");
      // Не оставляем пользователя на шаге «банк» с ошибкой — возвращаем к вводу ФИО.
      setPhase("last_name");
      return;
    }
    const submitEmail = email.trim();
    if (!canSkipEmailVerification) {
      if (!submitEmail) {
        setRegError("Укажите email для уведомлений.");
        return;
      }
      if (!/^\S+@\S+\.\S+$/.test(submitEmail)) {
        setRegError("Некорректный email.");
        return;
      }
    }
    if (jumpSbpEnabled) {
      if (!sbpBankId) {
        setRegError("Выберите банк для перевода по СБП (список из платёжного сервиса).");
        return;
      }
    }
    setRegSubmitting(true);
    try {
      const regPayload = {
        phone: phoneDigits,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: canSkipEmailVerification ? "" : submitEmail,
      };
      if (phoneProof) {
        regPayload.phone_proof = phoneProof;
      }
      if (jumpSbpEnabled && sbpBankId) {
        regPayload.sbp_bank_id = parseInt(String(sbpBankId), 10);
      }
      const data = await registerGiveawayParticipant(item.id, regPayload);
      const token = data.verification_token ?? null;
      saveGlobalProfile({
        phone: phoneDigits,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: canSkipEmailVerification ? "" : submitEmail,
        ...(jumpSbpEnabled && sbpBankId
          ? { sbp_bank_id: String(sbpBankId) }
          : {}),
      });
      if (hasMarketplaceJwtCredentials()) {
        try {
          const patchBody = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
          };
          if (!canSkipEmailVerification) patchBody.email = submitEmail;
          if (jumpSbpEnabled && sbpBankId) {
            patchBody.sbp_bank_id = parseInt(String(sbpBankId), 10);
          }
          const me = await patchAccountMe(patchBody);
          cacheGlobalProfileFromAccountMe(me);
        } catch {
          /* профиль останется только локально */
        }
      }
      if (token) {
        saveTokenForGiveaway(item.id, token);
        setParticipantToken(token);
        try {
          // 1) Получить/назначить ключевое слово сразу
          const p = await fetchMyParticipation(item.id, token);
          if (p?.assigned_keyword) setAssignedKeyword(String(p.assigned_keyword));
          // 2) Заново загрузить раздачу с token, чтобы шаг уже пришёл с подстановкой
          const g = await fetchGiveawayById(item.id, token);
          if (g) setItem(g);
        } catch {
          /* ignore */
        }
      }
      setPhase("steps");
      setIndex(0);
    } catch (e) {
      const msg = e.message ?? "Ошибка регистрации";
      setRegError(msg);
      // Если сервер отклонил из-за пустого имени/фамилии — возвращаем на шаг ФИО,
      // а не оставляем на «банке» с непонятной ошибкой про «пустое поле».
      if (!firstName.trim() || !lastName.trim() || /имя|фамили|пуст/i.test(msg)) {
        setPhase("last_name");
      }
    } finally {
      setRegSubmitting(false);
    }
  }

  function shotsForStep(stepIdx) {
    return stepShots[stepIdx] ?? [];
  }

  function requiredShotsForStep(st) {
    if (!st?.requires_screenshot) return 0;
    const n = Number(st?.shots);
    return n > 1 ? Math.min(n, 10) : 1;
  }

  function hasShotsForStep(stepIdx, shotsMap = stepShots) {
    const st = item?.steps?.[stepIdx];
    return (shotsMap[stepIdx] ?? []).length >= requiredShotsForStep(st);
  }

  async function refreshStepShotsFromServer() {
    if (!item?.id || !participantToken) return stepShots;
    const data = await fetchStepScreenshots(item.id, participantToken);
    const map = mapScreenshotsByStep(data.screenshots);
    setStepShots(map);
    return map;
  }

  async function ensureScreenshotForStep(stepIdx) {
    const steps = item.steps;
    const st = steps[stepIdx];
    if (!st?.requires_screenshot) return true;
    if (!participantToken) {
      setStepError("Сессия устарела. Обновите страницу или пройдите регистрацию снова.");
      return false;
    }
    if (hasShotsForStep(stepIdx)) return true;
    try {
      const map = await refreshStepShotsFromServer();
      if (hasShotsForStep(stepIdx, map)) return true;
    } catch {
      /* local state only */
    }
    const need = requiredShotsForStep(st);
    setStepError(
      need > 1
        ? `Добавьте ${need} фото для этого этапа.`
        : "Добавьте скриншот для этого этапа.",
    );
    return false;
  }

  async function handleScreenshotsSelected(fileList) {
    if (!item?.id || !participantToken) return;
    const arr = Array.from(fileList || []).filter((f) => f && f.size > 0);
    if (!arr.length) return;
    for (const file of arr) {
      if (file.size > MAX_SCREENSHOT_BYTES) {
        setStepError("Каждый файл не больше 10 МБ.");
        return;
      }
      const ct = (file.type || "").toLowerCase();
      if (!ALLOWED_SCREENSHOT_TYPES.has(ct)) {
        setStepError("Допустимы только JPEG, PNG и WebP.");
        return;
      }
    }
    setUploadBusy(true);
    setStepError(null);
    let uploadedOk = false;
    try {
      for (const file of arr) {
        const data = await uploadStepScreenshot(item.id, participantToken, index, file);
        const id = data.id;
        const url = data.image_url;
        if (id == null) continue;
        uploadedOk = true;
        setStepShots((prev) => {
          const cur = [...(prev[index] || [])];
          cur.push({ id, url: url ?? "" });
          return { ...prev, [index]: cur };
        });
      }
    } catch (e) {
      setStepError(e.message ?? "Ошибка загрузки");
      return;
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
    const steps = item?.steps;
    const isLast =
      Array.isArray(steps) && steps.length > 0 && index >= steps.length - 1;
    if (uploadedOk && isLast && steps[index]?.requires_screenshot) {
      await finishParticipation();
    }
  }

  async function removeStepScreenshot(shotId) {
    if (!item?.id || !participantToken) return;
    setUploadBusy(true);
    setStepError(null);
    try {
      await deleteStepScreenshot(item.id, participantToken, shotId);
      setStepShots((prev) => ({
        ...prev,
        [index]: (prev[index] || []).filter((s) => s.id !== shotId),
      }));
    } catch (e) {
      setStepError(e.message ?? "Не удалось удалить файл");
    } finally {
      setUploadBusy(false);
    }
  }

  async function goForward() {
    const steps = item.steps;
    const ok = await ensureScreenshotForStep(index);
    if (!ok) return;
    setStepError(null);
    setIndex((i) => Math.min(steps.length - 1, i + 1));
  }

  async function finishParticipation() {
    const steps = item.steps;
    const ok = await ensureScreenshotForStep(index);
    if (!ok) return;
    if (!participantToken) {
      setStepError("Сессия устарела. Обновите страницу или пройдите регистрацию снова.");
      return;
    }
    setStepError(null);
    setFinishBusy(true);
    try {
      let shotsMap = stepShots;
      try {
        shotsMap = await refreshStepShotsFromServer();
      } catch {
        /* local state */
      }
      const missing = steps.findIndex(
        (s, i) => s.requires_screenshot && !hasShotsForStep(i, shotsMap)
      );
      if (missing >= 0) {
        setStepError(`Загрузите скриншот для этапа ${missing + 1}.`);
        setIndex(missing);
        return;
      }
      await submitParticipationComplete(item.id, participantToken);
      setPhase("submitted");
    } catch (e) {
      const msg = e.message ?? "Не удалось отправить заявку";
      setStepError(msg);
      const match = msg.match(/этапов?:\s*([\d,\s]+)/i);
      if (match) {
        const first = Number.parseInt(match[1].trim().split(/[,\s]/)[0], 10);
        if (Number.isFinite(first) && first > 0) {
          setIndex(first - 1);
        }
      }
    } finally {
      setFinishBusy(false);
    }
  }

  if (error) {
    return (
      <p className={styles.error} role="alert">
        {error}
      </p>
    );
  }

  if (item === undefined) {
    return <p className={styles.loading}>Загрузка…</p>;
  }

  if (!item) {
    return <Navigate to="/" replace />;
  }

  if (phase === "loading") {
    return <p className={styles.loading}>Загрузка…</p>;
  }

  if (phase === "syncing") {
    return (
      <div className={styles.page}>
        <p className={styles.loading}>Подключаем сохранённые данные к этой раздаче…</p>
        <p className={styles.syncHint}>
          Регистрация на сайте запрашивается один раз; для новых раздач профиль подставляется
          автоматически.
        </p>
      </div>
    );
  }

  if (phase === "submitted") {
    return (
      <div className={`${styles.page} ${styles.pageSteps}`}>
        <div className={styles.top}>
          <h1 className={styles.title}>{item.title}</h1>
        </div>
        <div className={styles.card}>
          <span className={styles.stepLabel}>Готово</span>
          <h2 className={styles.stepTitle}>Заявка отправлена</h2>
          <p className={styles.stepBody}>
            Обработка заявки может занять <strong>до 12 часов</strong>. Нужно просто подождать — как
            только проверка завершится, вы узнаете результат.
          </p>
        </div>
        <div className={`${styles.nav} ${styles.navSingle}`}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => navigate("/")}
          >
            На главную
          </button>
        </div>
      </div>
    );
  }

  if (
    phase === "phone" ||
    phase === "phone_confirm" ||
    phase === "email_request" ||
    phase === "email_code" ||
    phase === "last_name" ||
    phase === "first_name" ||
    phase === "bank"
  ) {
    return (
      <div className={`${styles.page} ${styles.pageSteps}`}>
        <div className={styles.top}>
          <h1 className={styles.title}>{item.title}</h1>
        </div>

        <div className={styles.card}>
          {phase === "phone" && (
            <>
              <span className={styles.stepLabel}>Контакты</span>
              <h2 className={styles.stepTitle}>Номер телефона</h2>
              <p className={styles.stepBody}>
                Введите номер в формате +7 или 8 (Россия). На следующем шаге введите его ещё раз для
                проверки.
              </p>
              <label className={styles.fieldLabel} htmlFor="reg-phone">
                Телефон
              </label>
              <input
                id="reg-phone"
                className={styles.input}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+7 900 000-00-00"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => setPhone(formatPhoneRu(parsePhoneRu(phone)))}
              />
            </>
          )}

          {phase === "phone_confirm" && (
            <>
              <span className={styles.stepLabel}>Контакты</span>
              <h2 className={styles.stepTitle}>Повторите номер</h2>
              <p className={styles.stepBody}>
                Введите тот же номер ещё раз, чтобы исключить опечатку.
              </p>
              <label className={styles.fieldLabel} htmlFor="reg-phone2">
                Телефон ещё раз
              </label>
              <input
                id="reg-phone2"
                className={styles.input}
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+7 900 000-00-00"
                value={phoneConfirm}
                onChange={(e) => setPhoneConfirm(e.target.value)}
                onBlur={() => setPhoneConfirm(formatPhoneRu(parsePhoneRu(phoneConfirm)))}
              />
            </>
          )}

          {phase === "email_request" && (
            <>
              <span className={styles.stepLabel}>Email</span>
              <h2 className={styles.stepTitle}>Почта для кода</h2>
              <p className={styles.stepBody}>
                Мы отправим <strong>4 цифры</strong> на указанный адрес. Если аккаунт с таким номером
                уже есть — войдём через этот email.
              </p>
              <label className={styles.fieldLabel} htmlFor="reg-email-req">
                Email
              </label>
              <input
                id="reg-email-req"
                className={styles.input}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </>
          )}

          {phase === "email_code" && (
            <>
              <span className={styles.stepLabel}>Почта</span>
              <h2 className={styles.stepTitle}>Код из письма</h2>
              <p className={styles.stepBody}>
                Мы отправили код на <strong>{emailTrim}</strong>. Проверьте папку «Спам», если письма
                нет во входящих.
              </p>
              <label className={styles.fieldLabel} htmlFor="reg-otp">
                4 цифры
              </label>
              <input
                id="reg-otp"
                className={styles.input}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="0000"
                maxLength={4}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              />
            </>
          )}

          {phase === "last_name" && (
            <>
              <span className={styles.stepLabel}>Участник</span>
              <h2 className={styles.stepTitle}>Фамилия</h2>
              <p className={styles.stepBody}>
                Как в паспорте или как в банке для выплат — без сокращений.
              </p>
              <label className={styles.fieldLabel} htmlFor="reg-last">
                Фамилия
              </label>
              <input
                id="reg-last"
                className={styles.input}
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              {skipEmailSteps && !canSkipEmailVerification && !emailValid ? (
                <>
                  <label className={styles.fieldLabel} htmlFor="reg-email-skip">
                    Email для уведомлений
                  </label>
                  <input
                    id="reg-email-skip"
                    className={styles.input}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </>
              ) : null}
            </>
          )}

          {phase === "first_name" && (
            <>
              <span className={styles.stepLabel}>Участник</span>
              <h2 className={styles.stepTitle}>Имя</h2>
              <p className={styles.stepBody}>Имя для заявки и уведомлений.</p>
              <label className={styles.fieldLabel} htmlFor="reg-first">
                Имя
              </label>
              <input
                id="reg-first"
                className={styles.input}
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </>
          )}

          {phase === "bank" && (
            <>
              <span className={styles.stepLabel}>Выплата</span>
              <h2 className={styles.stepTitle}>Банк для СБП</h2>
              <p className={styles.stepBody}>
                Выплата идёт через СБП на ваш номер — выберите банк из списка платёжного сервиса.
              </p>
              <label className={styles.fieldLabel} htmlFor="reg-sbp-bank">
                Банк (куда зачислить на номер телефона)
              </label>
              <p className={styles.stepBodyMuted}>
                Выберите ваш банк из списка системы быстрых платежей — это нужно для автоматической
                выплаты.
              </p>
              <select
                id="reg-sbp-bank"
                className={styles.select}
                value={sbpBankId}
                onChange={(e) => setSbpBankId(e.target.value)}
                disabled={!sbpBanks.length}
              >
                {!sbpBanks.length && <option value="">Загрузка банков…</option>}
                {sbpBanks.length > 0 && <option value="">— выберите банк —</option>}
                {sbpBanks.map((b) => (
                  <option key={b.value} value={String(b.value)}>
                    {b.title}
                  </option>
                ))}
              </select>
            </>
          )}

          {regError && (
            <p className={styles.regError} role="alert">
              {regError}
            </p>
          )}
        </div>

        <div
          className={phase === "phone" ? `${styles.nav} ${styles.navSingle}` : styles.nav}
        >
          {phase === "phone" ? (
            <button
              type="button"
              className={styles.btnPrimary}
              disabled={callBusy || !phoneValid}
              onClick={continueFromPhone}
            >
              Далее
            </button>
          ) : phase === "phone_confirm" ? (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={callBusy}
                onClick={() => {
                  setRegError(null);
                  setPhase("phone");
                }}
              >
                Назад
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={callBusy || !phonesMatch}
                onClick={continueFromPhoneConfirm}
              >
                Далее
              </button>
            </>
          ) : phase === "email_request" ? (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={callBusy}
                onClick={() => {
                  setRegError(null);
                  setPhase("phone_confirm");
                }}
              >
                Назад
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={callBusy || !phoneValid || !emailValid}
                onClick={sendEmailCodeForGiveaway}
              >
                {callBusy ? "Отправка…" : "Отправить код на почту"}
              </button>
            </>
          ) : phase === "email_code" ? (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={callBusy}
                onClick={() => {
                  setRegError(null);
                  setPhase("email_request");
                }}
              >
                Назад
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={callBusy || otpCode.replace(/\D/g, "").length !== 4}
                onClick={submitEmailCodeForGiveaway}
              >
                {callBusy ? "Проверка…" : "Проверить код"}
              </button>
            </>
          ) : phase === "last_name" ? (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={callBusy}
                onClick={goBackFromLastName}
              >
                Назад
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={!lastName.trim() || (skipEmailSteps && !canSkipEmailVerification && !emailValid)}
                onClick={() => {
                  setRegError(null);
                  setPhase("first_name");
                }}
              >
                Далее
              </button>
            </>
          ) : phase === "first_name" ? (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={regSubmitting}
                onClick={() => {
                  setRegError(null);
                  setPhase("last_name");
                }}
              >
                Назад
              </button>
              {jumpSbpEnabled ? (
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={!firstName.trim() || (!canSkipEmailVerification && !emailValid)}
                  onClick={() => {
                    setRegError(null);
                    setPhase("bank");
                  }}
                >
                  Далее
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.btnPrimary}
                  disabled={regSubmitting || !firstName.trim() || (!canSkipEmailVerification && !emailValid)}
                  onClick={submitRegistration}
                >
                  {regSubmitting ? "Отправка…" : "Сохранить и продолжить"}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={regSubmitting}
                onClick={() => {
                  setRegError(null);
                  setPhase("first_name");
                }}
              >
                Назад
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                disabled={
                  regSubmitting ||
                  (jumpSbpEnabled && (!sbpBanks.length || !String(sbpBankId ?? "").trim()))
                }
                onClick={submitRegistration}
              >
                {regSubmitting ? "Отправка…" : "Сохранить и продолжить"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const steps = item.steps;
  const step = steps[index];
  const isFirst = index === 0;
  const isLast = index === steps.length - 1;
  const needShot = !!step.requires_screenshot;
  const needShotCount = requiredShotsForStep(step);
  const currentShots = shotsForStep(index);
  const kwIdx = item.keywordStepIndex ?? item.keyword_step_index;
  const isKeywordStep = Number.isFinite(Number(kwIdx)) && Number(kwIdx) === index;
  const resolvedKeyword = resolveKeywordForStep(index, item, assignedKeyword);
  const stepBodyText = formatStepBodyForDisplay(step.body, index, item, {
    assignedKeyword,
  });

  return (
    <div className={`${styles.page} ${styles.pageSteps}`}>
      <div className={styles.top}>
        <h1 className={styles.title}>{item.title}</h1>
      </div>

      {reworkNote ? (
        <div
          role="alert"
          style={{
            background: "#fff4e5",
            border: "1px solid #f0c17a",
            borderRadius: 12,
            padding: "12px 14px",
            margin: "0 0 12px",
            display: "flex",
            gap: 10,
            alignItems: "flex-start",
          }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }} aria-hidden>↩️</span>
          <div style={{ fontSize: ".9rem", lineHeight: 1.4 }}>
            <b>Вернули на доработку</b>
            <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{reworkNote}</div>
          </div>
        </div>
      ) : null}

      <div className={styles.card}>
        <span className={styles.stepLabel}>Шаг {index + 1}</span>
        <h2 className={styles.stepTitle}>{step.title}</h2>
        {isKeywordStep && resolvedKeyword ? (
          <div className={styles.keywordBox} role="note" aria-label="Ключевое слово">
            <div className={styles.keywordLabel}>Ключевое слово</div>
            <div className={styles.keywordRow}>
              <div className={styles.keywordValue}>{resolvedKeyword}</div>
              <button
                type="button"
                className={`${styles.keywordCopyBtn}${keywordCopied ? ` ${styles.keywordCopyBtnDone}` : ""}`}
                onClick={async () => {
                  const text = String(resolvedKeyword ?? "").trim();
                  if (!text) return;
                  try {
                    await navigator.clipboard.writeText(text);
                    setKeywordCopied(true);
                    window.setTimeout(() => setKeywordCopied(false), 2000);
                  } catch {
                    window.prompt("Скопируйте вручную:", text);
                  }
                }}
                aria-label={keywordCopied ? "Скопировано" : "Скопировать ключевое слово"}
                title={keywordCopied ? "Скопировано" : "Скопировать"}
              >
                {keywordCopied ? (
                  <Check className={styles.keywordCopyIcon} size={22} weight="bold" aria-hidden />
                ) : (
                  <Copy className={styles.keywordCopyIcon} size={22} weight="regular" aria-hidden />
                )}
              </button>
            </div>
          </div>
        ) : null}
        <p className={styles.stepBody}>{renderTextWithLinks(stepBodyText)}</p>
        {(step.illustration_url || "").trim() ? (
          <div className={styles.stepIllustration}>
            <img
              src={String(step.illustration_url).trim()}
              alt=""
              loading="lazy"
              className={styles.stepIllustrationImg}
            />
          </div>
        ) : null}

        {needShot && (
          <div className={styles.shotBlock}>
            <p className={styles.shotTitle}>
              {needShotCount > 1 ? `Нужно ${needShotCount} фото` : "Скриншот"}{" "}
              <span className={styles.shotRequired}>(обязательно)</span>
            </p>
            <p className={styles.shotHint}>
              {needShotCount > 1
                ? `Приложите ${needShotCount} фото для этого этапа (напр. фото коробки и скрин с телефона). `
                : ""}
              JPEG, PNG или WebP, до 10 МБ каждый. Лишние можно удалить до перехода дальше.
            </p>
            {currentShots.length > 0 ? (
              <ul className={styles.shotGrid} aria-label="Загруженные скриншоты">
                {currentShots.map((s) => (
                  <li key={s.id} className={styles.shotTile}>
                    <img
                      className={styles.shotThumb}
                      src={s.url}
                      alt=""
                      loading="lazy"
                    />
                    <button
                      type="button"
                      className={styles.shotRemove}
                      disabled={uploadBusy || finishBusy}
                      onClick={() => removeStepScreenshot(s.id)}
                      aria-label="Удалить скриншот"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            <input
              ref={fileRef}
              id="participate-step-files"
              className={styles.hiddenFileInput}
              type="file"
              accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
              multiple
              onChange={(e) => {
                handleScreenshotsSelected(e.target.files);
              }}
            />
            <button
              type="button"
              className={styles.uploadZone}
              disabled={uploadBusy || finishBusy}
              onClick={() => fileRef.current?.click()}
            >
              <span className={styles.uploadZoneIcon} aria-hidden>
                +
              </span>
              <span className={styles.uploadZoneText}>
                {currentShots.length ? "Добавить ещё" : "Выбрать скриншоты"}
              </span>
              <span className={styles.uploadZoneSub}>камера или галерея</span>
            </button>
          </div>
        )}

        {stepError && (
          <p className={styles.regError} role="alert">
            {stepError}
          </p>
        )}
      </div>

      <div className={styles.nav}>
        <button
          type="button"
          className={styles.btnSecondary}
          disabled={isFirst || uploadBusy || finishBusy}
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
        >
          Назад
        </button>
        {isLast ? (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={uploadBusy || finishBusy}
            onClick={finishParticipation}
          >
            {finishBusy ? "Отправка…" : uploadBusy ? "Загрузка…" : "Готово"}
          </button>
        ) : (
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={uploadBusy || finishBusy}
            onClick={goForward}
          >
            {uploadBusy ? "Загрузка…" : "Вперёд"}
          </button>
        )}
      </div>
    </div>
  );
}
